import { Box, Button, Flex, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import useSWR from 'swr'
import { V4_ADDRESS, V4_API_URL, V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { V4_BINDING } from 'views/TradingCompetitionV4/binding'
import { competitionV4Abi } from 'views/TradingCompetitionV4/abi'
import { competitionV3Abi } from '../abi'
import { V3_ADDRESS, V3_API_URL, fetcher } from '../config'
import { ContractContext, useContractBinding } from '../contract'
import { useV3Wallet } from '../hooks'
import { competitionPath, legacyPath } from '../routes'
import { Num } from '../ui'
import TokenAmount from './TokenAmount'
import { useV3Tx } from './useV3Tx'

type Claimable = { id: number; rewardToken: string; amount: string }
export type ClaimRow = Claimable & { legacy: boolean }

/**
 * What the wallet can claim and has created, from BOTH contracts (the previous one is tagged legacy). Until 2026-10-06 this was the "Your
 * competitions" box in the list hero; the list now shows only an "N to claim" chip and the rows with their Claim buttons live on Rewards & referral.
 * Same reads as before (same SWR keys, so the two pages share one cache).
 */
export function useYourClaims() {
  const { address, isConnected } = useV3Wallet()
  const a = address?.toLowerCase()
  const { apiUrl } = useContractBinding()
  const opts = { refreshInterval: 30000, shouldRetryOnError: false }
  const cur = useSWR(a ? `${V4_ENABLED ? V4_API_URL : apiUrl}/user/${a}` : null, fetcher, opts)
  const old = useSWR(a && V4_ENABLED ? `${V3_API_URL}/user/${a}` : null, fetcher, opts)
  const rows: ClaimRow[] = [
    ...((cur.data?.claimable ?? []) as Claimable[]).map((c) => ({ ...c, legacy: false })),
    ...((old.data?.claimable ?? []) as Claimable[]).map((c) => ({ ...c, legacy: true })),
  ]
  const created = (cur.data?.created?.length ?? 0) + (old.data?.created?.length ?? 0)
  const claimed = (cur.data?.claimed?.length ?? 0) + (old.data?.claimed?.length ?? 0)
  const refresh = () => {
    cur.mutate()
    old.mutate()
  }
  return { isConnected, address: a, rows, created, claimed, refresh }
}

/** One row per prize, each with its own Claim button (claims are the user's money): the same call the list hero made, to the contract the prize is on. */
function Rows() {
  const { rows, created, claimed, refresh } = useYourClaims()
  const { send, pending } = useV3Tx()
  const claim = async (c: ClaimRow) => {
    const useV4 = V4_ENABLED && !c.legacy
    const done = await send('Claim reward', {
      address: useV4 ? V4_ADDRESS : V3_ADDRESS,
      abi: useV4 ? competitionV4Abi : competitionV3Abi,
      functionName: 'withdrawRewardForIds',
      args: [[BigInt(c.id)]],
    })
    if (done) refresh()
  }
  return (
    <Box>
      {rows.length === 0 && <Text color="textSubtle" mt="12px">Nothing to claim right now. Prizes from finished competitions you won appear here.</Text>}
      {rows.length > 0 && (
        <Box mt="12px" style={{ display: 'grid', gap: 8 }} data-testid="v3-your-claims">
          <Text fontSize="12px" color="textSubtle">To claim <Num data-testid="v3-your-claim-count">{rows.length}</Num></Text>
          {rows.map((c) => (
            <Flex key={`${c.legacy ? 'l' : 'c'}${c.id}`} alignItems="center" justifyContent="space-between" style={{ gap: 12, minHeight: 48 }} data-testid="v3-your-claim">
              <Link href={c.legacy ? legacyPath(c.id) : competitionPath(c.id)} style={{ textDecoration: 'underline', minWidth: 0, fontSize: 15 }}>
                #{c.id}{c.legacy ? ' · legacy' : ''} · <TokenAmount raw={c.amount} token={c.rewardToken} />
              </Link>
              <Button scale="sm" disabled={pending} onClick={() => claim(c)} style={{ minHeight: 44, flex: 'none' }} data-testid="v3-your-claim-btn">Claim</Button>
            </Flex>
          ))}
        </Box>
      )}
      <Text fontSize="12px" color="textSubtle" mt="16px">Claimed <Num>{claimed}</Num> · created <Num>{created}</Num></Text>
    </Box>
  )
}

/** The claim rows under the binding the list page used (V4 when it is configured), so every call goes out exactly as it did from the list. */
export default function YourClaims() {
  return V4_ENABLED ? (
    <ContractContext.Provider value={V4_BINDING}>
      <Rows />
    </ContractContext.Provider>
  ) : (
    <Rows />
  )
}
